import {
  EntityId,
  FulfillmentTypes,
  PageEntity,
  PrinticularOrder,
  PrintServiceEntityHydrated,
  PrintServiceProductEntityHydrated,
  SettingsEntityHydrated,
} from "@jackfruit/common"
import { Address, AddressPayload } from "~/interfaces/entities/Address"
import { couponCodeStatus } from "~/interfaces/entities/Cart"
import { GiftCertificateEntity } from "~/interfaces/entities/GiftCertificate"
import { ImageEntity } from "~/interfaces/entities/Image"
import { ImageAITransformationOutputEntity } from "~/interfaces/entities/ImageAITransformationOutput"
import { ImageRegionEntity } from "~/interfaces/entities/ImageRegion"
import { ImageTransformationEntity } from "~/interfaces/entities/ImageTransformation"
import {
  LineItemEntity,
  LineItemMetaData,
} from "~/interfaces/entities/LineItem"
import { Payment } from "~/interfaces/entities/Payment"
import { PageType, ProductPageEntity } from "~/interfaces/entities/ProductPage"
import { StoreEntity } from "~/interfaces/entities/Store"
import { UploadEntity } from "~/interfaces/entities/Upload"
import { User } from "~/interfaces/entities/User"
import { ApplicationState } from "~/redux/state/application"
import { VNZAuthState } from "~/redux/state/VNZAuth"
import { convertToEntity, splitFullName } from "./Utils"
import { nanoid } from "@reduxjs/toolkit"

export interface OrderSerializerData {
  appUrl: string
  deviceToken: string
  application: ApplicationState
  page: PageEntity
  currency: string
  settings: SettingsEntityHydrated
  fulfillment: FulfillmentTypes
  giftCertificates: GiftCertificateEntity[]
  store: StoreEntity
  payment: Payment
  user: User
  address: Address
  lineItems: LineItemEntity[]
  productPages: ProductPageEntity[]
  imageRegions: ImageRegionEntity[]
  images: ImageEntity[]
  imageAITransformationOutputs: ImageAITransformationOutputEntity[]
  imageTransformations: ImageTransformationEntity[]
  uploads: UploadEntity[]
  printService: PrintServiceEntityHydrated
  printServiceProducts: PrintServiceProductEntityHydrated[]
  isFree: boolean
  couponCode: string
  couponCodeStatus: couponCodeStatus
  nonce: string
  vnzAuth: VNZAuthState
  attributionData: Record<string, string>
  shippingMethodId?: EntityId
}

interface AutopilotAddress extends AddressPayload {
  emailAddress?: string
  phoneNumber?: string
}

export class PrinticularSerializer {
  static serializeOrder(payload: OrderSerializerData) {
    const {
      appUrl,
      deviceToken,
      application,
      page,
      fulfillment,
      store,
      user,
      payment,
      address,
      lineItems,
      uploads,
      images,
      imageRegions,
      imageTransformations,
      imageAITransformationOutputs,
      currency,
      printService,
      printServiceProducts,
      couponCode,
      giftCertificates,
      nonce,
      productPages,
      vnzAuth,
      attributionData,
      shippingMethodId,
    } = payload

    const isGiftCertificate = lineItems.some(
      (lineItem: LineItemEntity) => lineItem.giftCertificateId !== undefined
    )

    const fullName = splitFullName(address.name ?? "")

    const { referralCode, referralPartner } = application.urlParams
    const { accessToken: verizonToken } = vnzAuth

    const addressForAutopilot: AutopilotAddress = {
      firstName: fullName.firstName,
      lastName: fullName.lastName,
      addressLine1: address.line1,
      addressLine2: address.line2,
      city: address.city,
      state: address.state,
      postCode: address.postcode,
      emailAddress: user.email,
      phoneNumber: user.phoneNumber,
    }

    const attributes: any = {
      appUrl,
      deviceToken,
      currency: currency,
      clientName: application.name,
      clientVersion: application.appVersion,
      payment: { type: payment.type },
      country: fulfillment === "pickup" ? store.countryCode : address.country,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent,
      couponCode,
      nonce,
      metaData: {
        web: {
          origin: appUrl,
          appVersion: application.appVersion,
          appId: application.id,
          pageId: page.id,
          pageSlug: page.slug,
        },
      },
      ...(referralCode && { referralCode }),
      ...(referralPartner && { referralPartner }),
      ...(verizonToken && { verizonToken }),
      ...addressForAutopilot,
    }

    const orderTrackingCodes = Object.entries(attributionData).map(
      ([key, value]) => {
        return {
          id: nanoid(),
          type: "OrderTrackingCode",
          attributes: {
            trackingType: key === "url" ? "url" : "query-string",
            trackingKey: key,
            trackingValue: value,
            metaData: {},
          },
        }
      }
    )

    const relationships: any = {
      printService: {
        data: {
          id: printService.remoteId,
          type: "PrintService",
        },
      },
      lineItems: {
        data: lineItems.map(lineItem => ({
          id: lineItem.id,
          type: "LineItem",
        })),
      },
      orderTrackingCodes: {
        data: orderTrackingCodes.map(orderTrackingCode => ({
          id: orderTrackingCode.id,
          type: "OrderTrackingCode",
        })),
      },
    }

    if (fulfillment === "pickup") {
      relationships.store = {
        data: { id: store.id, type: "Store" },
      }
    }

    if (shippingMethodId && fulfillment === "delivery") {
      relationships.shippingMethod = {
        data: { id: shippingMethodId, type: "PrintServiceShippingMethod" },
      }
    }

    const data = {
      type: "Order",
      attributes,
      relationships,
    }

    const lineItemsForAutopilot = lineItems.map(lineItem => {
      const product = printServiceProducts.find(
        printServiceProduct => printServiceProduct.id === lineItem.productId
      )

      if (isGiftCertificate) {
        return {
          id: lineItem.id,
          type: "LineItem",
          attributes: {
            quantity: lineItem.quantity,
            orientation: lineItem.orientation,
            giftCertificate: {
              toEmailAddress: giftCertificates[0].email,
              toName: giftCertificates[0].name,
              message: giftCertificates[0].message,
            },
          },
          relationships: {
            product: {
              data: {
                id: product?.remoteId.toString(),
                type: "Product",
              },
            },
          },
        }
      } else {
        const imageUrls = lineItem.renderedUploadIds.map(uploadId => {
          const upload = uploads.find(upload => upload.id === uploadId)
          const image = images.find(image => image.id === upload?.imageId)

          let url = image?.externalUrl
          if (lineItem.isDocument && upload?.location) {
            url = upload.location
          }

          return {
            url,
            mimeType: upload?.type,
            name: upload?.name,
          }
        })

        const productTemplateId = lineItem.productTemplateId
        const productTemplateVariantId = lineItem.productTemplateVariantId

        const productTemplate = productTemplateId
          ? {
              productTemplate: {
                data: {
                  id: productTemplateId,
                  type: "ProductTemplate",
                },
              },
            }
          : {}

        const productTemplateVariant = productTemplateVariantId
          ? {
              productTemplateVariant: {
                data: {
                  id: productTemplateVariantId,
                  type: "ProductTemplateVariant",
                },
              },
            }
          : {}

        // copy line item metaData for making it mutable
        const lineItemMetaData: LineItemMetaData = {
          changeQuantityButtonClicked: false,
          quantityPackSelected: false,
          quantityPackSelectedName: "",
          quantityPackSelectedValue: "",
          ...lineItem.metaData,
        }

        if (lineItem.renderedUploadIds.length > 0) {
          const imageData = lineItem.productPageIds.flatMap(productPageId => {
            const productPage = productPages.find(
              productPage => productPage.id === productPageId
            )
            const result = (productPage?.imageRegionIds ?? []).map(
              imageRegionId => {
                const imageRegion = imageRegions.find(
                  imageRegion => imageRegion.id === imageRegionId
                )

                if (!imageRegion?.uploadId) {
                  return {}
                }

                const upload = uploads.find(
                  upload => upload.id === imageRegion.uploadId
                )
                const image = images.find(image => image.id === upload?.imageId)
                const imageTransform = imageTransformations.find(
                  imageTransform =>
                    imageTransform.id === image?.imageTransformId
                )

                return {
                  url: upload?.location,
                  mimeType: upload?.type,
                  height: image?.height,
                  width: image?.width,
                  hash: upload?.hash,
                  fileName: upload?.name,
                  fileLength: upload?.size,
                  ...(imageTransform?.imageFilterName && {
                    filterName: imageTransform.imageFilterName,
                  }),
                }
              }
            )

            return result
          })

          if (imageData.length > 0) {
            lineItemMetaData.imageData = imageData
          }
        }

        const imageAITransformationOutputForLineItem =
          imageAITransformationOutputs.find(
            imageAITransformationOutput =>
              imageAITransformationOutput.lineItemId === lineItem.id
          )

        if (imageAITransformationOutputForLineItem) {
          lineItemMetaData.imageTransformTypeId =
            imageAITransformationOutputForLineItem.imageTransformTypeId
        }

        let documentAttributes = {}
        if (lineItem.isDocument) {
          documentAttributes = {
            pageCount: lineItem.documentPageCount,
          }
        }

        return {
          id: lineItem.id,
          type: "LineItem",
          attributes: {
            quantity: lineItem.quantity,
            orientation: lineItem.orientation,
            images: { original: imageUrls },
            metaData: lineItemMetaData,
            ...documentAttributes,
          },
          relationships: {
            product: {
              data: {
                id: product?.remoteId.toString(),
                type: "Product",
              },
            },
            ...productTemplate,
            ...productTemplateVariant,
          },
        }
      }
    })

    const included = [...lineItemsForAutopilot, ...orderTrackingCodes]

    const order: any = {
      data,
      included,
    }

    return order
  }

  static deserializeOrder(printicularOrder: any) {
    const orderEntity = convertToEntity<any>(printicularOrder.data)
    const deserializedOrder: PrinticularOrder = {
      ...orderEntity,
    } as PrinticularOrder

    const lineItems = printicularOrder.included.filter(
      (included: any) => included.type === "LineItem"
    )

    const deserializedLineItems = lineItems.map((lineItem: any) => {
      const productId = lineItem.relationships.product.data.id
      const product = printicularOrder.included.find(
        (included: any) =>
          included.type === "Product" && included.id === productId
      )

      const entities = convertToEntity<any>(lineItem)

      if (product) {
        const deserializedProduct = convertToEntity(product)
        entities.product = deserializedProduct
      }

      return entities
    })
    deserializedOrder.lineItems = deserializedLineItems

    const orderShippingMethodOptions = printicularOrder.included.filter(
      (included: any) => included.type === "OrderShippingMethodOption"
    )
    const deserializedOrderShippingMethodOptions =
      orderShippingMethodOptions.map((option: any) => convertToEntity(option))

    deserializedOrder.orderShippingMethodOptions =
      deserializedOrderShippingMethodOptions

    const printServieShippingMethod = printicularOrder.included.find(
      (included: any) => included.type === "PrintServiceShippingMethod"
    )

    if (printServieShippingMethod) {
      deserializedOrder.printServiceShippingMethod = convertToEntity(
        printServieShippingMethod
      )
    }

    return deserializedOrder
  }

  static addFakeImagesForDryRun(
    serializedPayload: any,
    orderData: OrderSerializerData
  ) {
    // create a deep copy to avoid mutating the original
    const newPayload = JSON.parse(JSON.stringify(serializedPayload))

    newPayload.included.forEach(
      (included: any, includedIndex: number, originalIncluded: any) => {
        if (included.type === "LineItem") {
          const { id } = included
          const lineItem = orderData.lineItems.find(
            lineItem => lineItem.id === id
          )

          if (lineItem?.isDocument) {
            const documentUploadId = lineItem.documentUploadId
            const uploadEntity = orderData.uploads.find(
              upload => upload.id === documentUploadId
            )
            originalIncluded[includedIndex].attributes.images.original = [
              {
                url: "dryRunImage",
                name: uploadEntity?.name,
                mimeType: uploadEntity?.type,
              },
            ]
          } else if (
            lineItem?.productPageIds &&
            lineItem.productPageIds.length > 0
          ) {
            const lineItemProductPages = lineItem.productPageIds.map(pageId => {
              return orderData.productPages.find(
                productPage => productPage.id === pageId
              )
            })

            const productPageRequiringImages = lineItemProductPages.filter(
              productPage => {
                const isVirtualPage = productPage?.isVirtualPage === true

                // don't send virtual page to the server
                if (isVirtualPage) {
                  return false
                }

                const imageRegions = productPage?.imageRegionIds || []
                const hasUserImages = imageRegions.length > 0

                const isNormalProductPage =
                  productPage?.pageType === PageType.Page

                const isDefaultProductPage = !productPage?.pageType

                return (
                  isNormalProductPage || isDefaultProductPage || hasUserImages
                )
              }
            )

            const isGiftCertificate =
              originalIncluded[includedIndex].attributes.giftCertificate !==
              undefined

            if (!isGiftCertificate) {
              originalIncluded[includedIndex].attributes.images.original =
                productPageRequiringImages.map(_page => {
                  return { url: "dryRunImage" }
                })
            }
          }
        }
      }
    )

    return newPayload
  }
}
